/**
 * Market recommendation
 *
 * Le cookie `localization` de Shopify vit un an : une fois un visiteur assigne
 * au mauvais marche, il n'est jamais reevalue. On interroge donc la detection
 * geo de Shopify (basee sur l'IP, independante du cookie) et on propose la
 * bascule quand la DEVISE du pays detecte differe de la devise courante.
 * Deux pays d'une meme devise (France / Belgique) ne declenchent rien.
 *
 * Placement : le bas de l'ecran est dispute par plusieurs surcouches qui
 * passent toutes au-dessus de nous (banniere cookies Shopify a z-index
 * 2000000, formulaire Klaviyo -10% a 90000, sticky add-to-cart a 50). Plutot
 * que de coder en dur leurs selecteurs, qui changent, on teste si la position
 * visee est reellement recouverte : on se pose en bas a droite si c'est libre,
 * sinon en haut sous l'en-tete, sinon on attend. Un seul encart a la fois,
 * jamais deux surcouches empilees.
 */
(() => {
  const root = document.querySelector('[data-market-reco]');
  const dataEl = document.querySelector('[data-market-reco-data]');
  if (!root || !dataEl) return;
  if (window.Shopify && window.Shopify.designMode) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (error) {
    return;
  }

  const STORAGE_KEY = 'cderma:market-reco';
  const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;
  // Assez long pour que la banniere cookies et le formulaire Klaviyo -10%
  // soient deja poses : le premier placement est alors le bon et le bandeau
  // ne se deplace pas sous les yeux du visiteur.
  const REVEAL_DELAY = 2500;
  const RETRY_MS = 500;
  const GUARD_MS = 800;
  const GUARD_WINDOW_MS = 30000;
  const ENDPOINT =
    '/browsing_context_suggestions.json' +
    '?source=geolocation_recommendation' +
    '&show_geolocation_recommendations=true' +
    '&country%5Benabled%5D=true';

  const readStore = () => {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const isDismissed = (code) => {
    const until = readStore()[code];
    return typeof until === 'number' && until > Date.now();
  };

  const dismiss = (code) => {
    const store = readStore();
    store[code] = Date.now() + DISMISS_MS;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (error) {
      // stockage indisponible (navigation privee) : on masque pour la session
    }
  };

  const fill = (template, token, value) => String(template).split(token).join(value);

  const detectCountry = async () => {
    const response = await fetch(ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;

    const json = await response.json();
    const detected = json && json.detected_values && json.detected_values.country;
    return detected && detected.handle ? detected.handle : null;
  };

  /* -------------------------------------------------------------------------
     Placement
     ---------------------------------------------------------------------- */

  // Le sticky add-to-cart des fiches produit occupe toute la largeur en bas de
  // l'ecran quand il porte .is-visible. On souleve le bandeau de sa hauteur.
  const stickyAtc = document.querySelector('[data-sticky-atc]');

  const syncLift = () => {
    if (!stickyAtc) return;
    const shown =
      stickyAtc.classList.contains('is-visible') &&
      !stickyAtc.classList.contains('is-hidden-by-drawer');
    const height = shown ? Math.round(stickyAtc.getBoundingClientRect().height) : 0;
    root.style.setProperty('--market-reco-lift', `${height}px`);
  };

  const setTopOffset = () => {
    const header = document.querySelector('.header');
    const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
    root.style.setProperty('--market-reco-top', `${Math.max(16, bottom + 12)}px`);
  };

  const ownZIndex = () => {
    const z = parseInt(window.getComputedStyle(root).zIndex, 10);
    return Number.isNaN(z) ? 0 : z;
  };

  // Remonte depuis l'element touche : y a-t-il une surcouche fixe qui peint
  // au-dessus de nous ? `z-index: auto` compte pour 0, donc l'en-tete collant
  // du theme (z-index 7) ou le sticky add-to-cart (50) ne comptent pas.
  //
  // Piege de test : la barre d'apercu Shopify est un popover en top layer, qui
  // peint au-dessus de tout sans z-index. Elle masque donc les vraies
  // surcouches au test de collision, mais uniquement en mode apercu. Toute
  // verification de placement doit se faire sur le theme publie.
  const coveredBy = (el, myZ) => {
    let node = el;
    while (node && node !== document.body && node !== document.documentElement) {
      if (node === root) return false;
      const style = window.getComputedStyle(node);
      if (style.position === 'fixed' || style.position === 'sticky') {
        const z = parseInt(style.zIndex, 10);
        if (!Number.isNaN(z) && z >= myZ) return true;
      }
      node = node.parentElement;
    }
    return false;
  };

  // On neutralise le hit-testing du bandeau le temps de la mesure, pour que
  // elementFromPoint renvoie ce qu'il y a dessous et non lui-meme. Necessaire
  // aussi quand il est deja visible, donc en pointer-events: auto.
  const positionIsFree = () => {
    const previousPointerEvents = root.style.pointerEvents;
    root.style.pointerEvents = 'none';
    try {
      return measureIsFree();
    } finally {
      root.style.pointerEvents = previousPointerEvents;
    }
  };

  const measureIsFree = () => {
    const rect = root.getBoundingClientRect();
    const viewportHeight = document.documentElement.clientHeight;
    const viewportWidth = document.documentElement.clientWidth;
    if (!rect.width || !rect.height) return false;
    if (rect.top < 0 || rect.bottom > viewportHeight) return false;
    if (rect.left < 0 || rect.right > viewportWidth) return false;

    const inset = 10;
    const points = [
      [rect.left + rect.width / 2, rect.top + rect.height / 2],
      [rect.left + inset, rect.top + inset],
      [rect.right - inset, rect.top + inset],
      [rect.left + inset, rect.bottom - inset],
      [rect.right - inset, rect.bottom - inset],
    ];

    const myZ = ownZIndex();
    return points.every(([x, y]) => !coveredBy(document.elementFromPoint(x, y), myZ));
  };

  // Essaie le bas a droite, puis le haut sous l'en-tete. Le contenu doit deja
  // etre en place : la hauteur du bandeau depend du texte.
  const findPlacement = () => {
    syncLift();
    setTopOffset();
    root.hidden = false;

    root.classList.remove('market-reco--top');
    if (positionIsFree()) return true;

    root.classList.add('market-reco--top');
    if (positionIsFree()) return true;

    root.classList.remove('market-reco--top');
    root.hidden = true;
    return false;
  };

  let stickyAtcWatched = false;

  const watchStickyAtc = () => {
    if (!stickyAtc || stickyAtcWatched) return;
    stickyAtcWatched = true;
    new MutationObserver(syncLift).observe(stickyAtc, {
      attributes: true,
      attributeFilter: ['class'],
    });
    window.addEventListener('resize', syncLift);
  };

  /* -------------------------------------------------------------------------
     Affichage
     ---------------------------------------------------------------------- */

  const fillContent = (code, country) => {
    const title = root.querySelector('[data-market-reco-title]');
    const text = root.querySelector('[data-market-reco-text]');
    const confirm = root.querySelector('[data-market-reco-confirm]');
    const dismissBtn = root.querySelector('[data-market-reco-dismiss]');
    if (!title || !text || !confirm || !dismissBtn) return false;

    title.textContent = fill(data.strings.title, '%%currency%%', country.currency);
    text.textContent = fill(data.strings.text, '%%country%%', country.name);
    confirm.textContent = fill(data.strings.confirm, '%%currency%%', country.currency);
    confirm.value = code;
    dismissBtn.textContent = fill(data.strings.dismiss, '%%currency%%', data.current.currency);

    dismissBtn.addEventListener('click', () => {
      dismiss(code);
      root.classList.remove('market-reco--visible');
      window.setTimeout(() => {
        root.hidden = true;
      }, 240);
    });

    return true;
  };

  const show = () => {
    window.requestAnimationFrame(() => {
      root.classList.add('market-reco--visible');
    });
  };

  const reveal = (code, country) => {
    if (!fillContent(code, country)) return;

    // Une surcouche peut arriver APRES nous (le formulaire Klaviyo se declenche
    // au bout de quelques secondes). On surveille donc un moment et on se
    // replace si on se retrouve recouvert.
    const guard = () => {
      let elapsed = 0;
      const timer = window.setInterval(() => {
        elapsed += GUARD_MS;

        if (root.hidden) {
          window.clearInterval(timer);
          return;
        }

        if (!positionIsFree()) {
          window.clearInterval(timer);
          root.classList.remove('market-reco--visible');
          attempt();
          return;
        }

        if (elapsed >= GUARD_WINDOW_MS) window.clearInterval(timer);
      }, GUARD_MS);

      window.addEventListener('pagehide', () => window.clearInterval(timer), { once: true });
    };

    // On reessaie sans limite de temps plutot que d'afficher quand meme : un
    // bandeau pose sous une autre surcouche serait invisible, donc inutile.
    const attempt = () => {
      if (!findPlacement()) {
        window.setTimeout(attempt, RETRY_MS);
        return;
      }

      watchStickyAtc();
      show();
      guard();
    };

    attempt();
  };

  const init = async () => {
    let code;
    try {
      code = await detectCountry();
    } catch (error) {
      return;
    }

    if (!code || code === data.current.country) return;

    const country = data.countries[code];
    if (!country) return;
    if (country.currency === data.current.currency) return;
    if (isDismissed(code)) return;

    window.setTimeout(() => reveal(code, country), REVEAL_DELAY);
  };

  init();
})();
